import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * 한시 무료 이벤트(050) 참가 마커 스토어.
 *
 * 참가 = `/event` 페이지 진입(spec FR-002 — 클릭만으로 성립, 입력 없음). 마커는 localStorage 에
 * 존재 여부만 저장해 재방문에도 유지된다(FR-003 — sessionStorage 면 브라우저 재방문에 유실).
 *
 * <b>자격이 아니라 UX 분기 신호다(R2)</b>: 전원 무료 이벤트라 서버는 이벤트 활성 여부만 강제하고
 * 마커를 검증하지 않는다 — 위조로 얻는 것은 정당한 무료 발급뿐.
 *
 * <b>화면 분기는 참가 마커가 아니라 "무료 의도"가 담당한다(2026-08-22 확정 — 심사 UI 불변 요건)</b>:
 * 카드사 심사 기준이 "사전 전달한 결제 경로 캡처와 UI 동일"이라, 장바구니 화면에는 결제/무료 버튼이
 * 동시에 떠서도, 마커(이벤트 페이지 열람만으로 생김) 때문에 화면이 바뀌어서도 안 된다. 그래서:
 *
 * - 무료 의도(freeIntent)는 <b>이벤트 페이지의 무료 CTA 클릭 시에만</b> 켜지고(sessionStorage — 탭 세션 한정),
 * - <b>홈(`/`) 진입 시 리셋</b>된다 — 심사자가 이벤트를 구경한 뒤 홈부터 결제 경로를 다시 밟으면
 *   장바구니는 항상 결제 전용 화면(캡처와 동일)이다.
 * - 장바구니는 의도 ON 이면 무료 버튼만, OFF 면 결제 버튼만 노출한다(한 화면 한 버튼).
 */

/** 참가 마커 localStorage 키 — 이벤트 식별자 포함(차기 이벤트와 충돌 방지). */
const MARKER_KEY = 'ipsi:event:2026-free-review';

/** 무료 의도 sessionStorage 키 — 탭 세션 한정(브라우저 재시작 시 소멸 = 결제 기본값 복귀). */
const INTENT_KEY = 'ipsi:event:2026-free-review:intent';

/** 마커 존재 여부 조회 — SSG 빌드·프라이빗 모드 등 storage 불가 환경은 false(비참가 폴백). */
function readMarker(): boolean {
  try {
    return localStorage.getItem(MARKER_KEY) !== null;
  } catch {
    return false;
  }
}

/** 무료 의도 조회 — storage 불가 환경은 false(결제 기본값 폴백). */
function readIntent(): boolean {
  try {
    return sessionStorage.getItem(INTENT_KEY) !== null;
  } catch {
    return false;
  }
}

export const useEventStore = defineStore('event', () => {
  /** 이벤트 참가 여부(마커 존재 여부) — 통계·안내용. 화면 분기에는 쓰지 않는다(freeIntent 담당). */
  const participating = ref(readMarker());

  /** 무료 수령 의도 — 이벤트 페이지 무료 CTA 를 눌러 이어온 흐름인지. 장바구니 렌더 분기의 유일 신호. */
  const freeIntent = ref(readIntent());

  /** 참가 마커 저장 — `/event` 진입 시 호출(멱등). storage 불가 환경에서도 세션 내 상태는 유지한다. */
  function participate(): void {
    try {
      localStorage.setItem(MARKER_KEY, new Date().toISOString());
    } catch {
      // storage 거부(프라이빗 모드 등) — 메모리 상태만으로 이번 세션의 분기는 성립시킨다.
    }
    participating.value = true;
  }

  /** 무료 의도 ON — 이벤트 페이지의 무료 CTA("대학 선택하고 무료로 받기") 클릭 시에만 호출한다. */
  function startFreeIntent(): void {
    try {
      sessionStorage.setItem(INTENT_KEY, '1');
    } catch {
      // storage 거부 — 메모리 상태만으로 이번 세션의 분기는 성립시킨다.
    }
    freeIntent.value = true;
  }

  /** 무료 의도 OFF — 홈(`/`) 진입 시 라우터 가드가 호출한다(결제 기본값 복귀 — 심사 UI 불변). */
  function clearFreeIntent(): void {
    try {
      sessionStorage.removeItem(INTENT_KEY);
    } catch {
      // storage 거부 — 메모리 상태만 리셋한다.
    }
    freeIntent.value = false;
  }

  return { participating, freeIntent, participate, startFreeIntent, clearFreeIntent };
});
